import { HttpClient } from '@angular/common/http';
import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService } from '@app/_services/configuration.service';
import { MsalService } from '@azure/msal-angular';
import { filter, take } from 'rxjs/operators';

declare const gapi: any;
declare const google: any;

/** Must match InterfaceEmailService refresh scope and Azure app registration. */
const MICROSOFT_OAUTH_SCOPE =
  'openid profile offline_access User.Read Mail.Read';

@Component({
  selector: 'kupari-oauth',
  templateUrl: './oauth.component.html',
  styleUrls: ['./oauth.component.scss'],
})
export class OAuthComponent implements OnInit {
  private googleAuthInstance: any;
  private tokenClient: any;
  @Input() type: string = 'ANY';
  @Input() loggedIn: string = '';
  @Input() redirectParams: any;
  @Output() onAuth: EventEmitter<any> = new EventEmitter<any>();
  @Output() onLogout: EventEmitter<any> = new EventEmitter<any>();
  constructor(
    private msalService: MsalService,
    private router: Router,
    private route: ActivatedRoute,
    private configuration: ConfigurationService,
    private http: HttpClient,
  ) {}

  ngOnInit() {
    if (this.type == 'ANY' || this.type == 'MICROSOFT') {
      this.initializeMicrosoftAuth();
    }
    if (this.type == 'ANY' || this.type == 'GOOGLE') {
      this.loadGapiScript()
        .then(() => {
          this.initializeGoogleLogin();
        })
        .catch((error) => {
          console.error('Error loading Google API script', error);
        });
    }
  }

  loadGapiScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject('Google API script failed to load');
      document.body.appendChild(script);
    });
  }

  initializeGoogleAuth() {
    gapi.load('auth2', () => {
      gapi.auth2
        .init({
          client_id:
            '83217889293-fmnn9td3d4komn44dmgke5td96d1pmcv.apps.googleusercontent.com',
          scope: 'https://www.googleapis.com/auth/gmail.readonly',
          redirect_uri:
            window.location.origin + '/oauth?' + this.redirectParams,
          access_type: 'offline',
        })
        .then(() => {
          this.googleAuthInstance = gapi.auth2.getAuthInstance();
        })
        .catch((err: any) => {
          console.error('ERROR', err);
        });
    });
  }

  /** Same URI used for authorize redirect and token exchange (must match Azure + backend Microsoft:RedirectUrl). */
  private getMicrosoftRedirectUri(): string {
    return 'http://localhost:4200/oauth';
    // return this.configuration.microsoftOAuthRedirectUrl;
  }

  initializeMicrosoftAuth() {
    this.route.queryParams
      .pipe(
        filter((params) => !!params['code']),
        take(1),
      )
      .subscribe((params) => {
        const code = params['code'] as string;
        let redirectParams: { returnUrl?: string; interfaceId?: number };
        try {
          redirectParams = JSON.parse(
            sessionStorage.getItem('redirectParams') ?? '{}',
          );
        } catch {
          console.error('Invalid redirectParams in sessionStorage');
          return;
        }
        const codeVerifier = sessionStorage.getItem('code_verifier');
        const redirectUri = this.getMicrosoftRedirectUri();
        console.log(redirectUri);
        this.http
          .post(this.configuration.apiUrl + '/interface/email/microsoft/oauth', {
            code,
            redirectUri,
            codeVerifier,
            returnUrl: redirectParams.returnUrl ?? '/',
            interfaceId: redirectParams.interfaceId ?? 0,
          })
          .subscribe({
            next: (body) => {
              sessionStorage.setItem('microsoftToken', JSON.stringify(body));
              const target = redirectParams.returnUrl?.startsWith('/')
                ? redirectParams.returnUrl
                : '/' + (redirectParams.returnUrl ?? '');
              this.router.navigateByUrl(target);
            },
            error: (err) => console.error('Microsoft token exchange failed', err),
          });
      });

    this.storeAuthorizationCode();
    this.msalService.instance.initialize().catch((err: any) => {
      console.log(err);
    });
  }

  initializeGoogleLogin() {
    this.tokenClient = google.accounts.oauth2.initCodeClient({
      client_id:
        '83217889293-fmnn9td3d4komn44dmgke5td96d1pmcv.apps.googleusercontent.com',
      scope:
        'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly',
      access_type: 'offline',
      prompt: 'consent',
      callback: (response: any) => {
        if (response.error) {
          console.error('Error retrieving authorization code:', response);
          return;
        }
        try {
          this.onAuth.emit({
            type: 'google',
            response: response,
          });
        } catch (error: any) {
          console.log(error);
        }
      },
    });
  }

  handleCredentialResponse(response: any) {
    // Reserved for credential-style Google sign-in
  }

  loginWithGoogle() {
    this.tokenClient.requestCode();
  }

  loginWithMicrosoft() {
    this.startLoginFlow().catch((err) => console.log(err));
  }

  handleGoogleSignInSuccess(googleUser: any) {
    const profile = googleUser.getBasicProfile();
    console.log('Google User: ', profile.getName(), profile.getEmail());
  }

  handleMicrosoftSignInSuccess(account: any) {
    console.log('Microsoft User: ', account.name, account.username);
  }

  logout() {
    // Let the parent clear Key Vault + DB for this interface. Do not call MSAL popup here — that signs
    // the user out of Microsoft globally and does not remove Avux-stored credentials.
    if (this.type === 'GOOGLE' || this.type === 'MICROSOFT') {
      this.onLogout.emit({ type: this.type });
    }
  }

  storeAuthorizationCode(): void {
    const fragment = window.location.hash;
    if (fragment && fragment.includes('code=')) {
      const code = this.getQueryParamFromFragment(fragment, 'code');
      const state = this.getQueryParamFromFragment(fragment, 'state');
      if (code) {
        localStorage.setItem('authorization_code', code);
        window.location.hash = '';
        if (state) {
          const parts = state.split('|');
          const jsonPart = parts.length > 1 ? parts[1] : parts[0];
          try {
            JSON.parse(jsonPart);
          } catch (error) {
            console.error('Invalid state parameter JSON part:', error);
          }
        }
      }
    }
  }

  getAndRemoveAuthorizationCode(): string | null {
    const code = localStorage.getItem('authorization_code');
    if (code) {
      localStorage.removeItem('authorization_code');
    }
    return code;
  }

  private getQueryParamFromFragment(
    fragment: string,
    param: string,
  ): string | null {
    const params = new URLSearchParams(fragment.substring(1));
    return params.get(param);
  }

  generateCodeVerifier(): string {
    const randomArray = new Uint8Array(32);
    window.crypto.getRandomValues(randomArray);
    return this.base64UrlEncode(randomArray);
  }

  base64UrlEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode.apply(null, buffer as any))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    return this.base64UrlEncode(hashArray);
  }

  async startLoginFlow(): Promise<void> {
    // const clientId = this.configuration.microsoftOAuthClientId?.trim();
    const clientId = 'ffe8f16f-a691-4b91-bcb1-25174a85e336';
    if (!clientId) {
      console.error(
        'Microsoft OAuth client id is not configured (Microsoft:OAuthClientId / microsoftOAuthClientId).',
      );
      return;
    }

    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    sessionStorage.setItem('code_verifier', codeVerifier);
    sessionStorage.setItem(
      'redirectParams',
      JSON.stringify(this.redirectParams ?? {}),
    );

    const redirectUri = this.getMicrosoftRedirectUri();
    console.log(redirectUri);
    const authority =
      this.configuration.microsoftOAuthAuthority?.trim() ||
      'https://login.microsoftonline.com/common';
    console.log(redirectUri,encodeURIComponent(redirectUri));

    const authorizationUrl =
      `${authority}/oauth2/v2.0/authorize?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(MICROSOFT_OAUTH_SCOPE)}` +
      `&code_challenge=${encodeURIComponent(codeChallenge)}` +
      `&code_challenge_method=S256`;

    window.location.href = authorizationUrl;
  }
}
